import * as components from '../../app/services/ComponentService';
import { normalizeJson } from '../helpers';
import tinycolor from 'tinycolor2';
import * as jobs from "../../app/services/JobService";
import { defineStore } from "pinia";
import { useCommonStore } from "./common";
import { useRepo } from "pinia-orm";
import Component from "../../app/models/Component";
import FilterGroup from "../../app/models/FilterGroup";

function initialState() {
    return {
        preview: {
            type: 'desktop',
        },
        settings: {
            rounding: 0,
            padding: 0,
            display_font: 'System Serif',
            text_font: 'Inter',
            primary_color: { h: 180, s: 0.5, l: 0.5, a: 1 },
            secondary_color: { h: 0, s: 0, l: 0.2, a: 1 },
        },
        addingComponent: false,
        configuringComponent: false,
        configuringHeader: false,
        fetchingComponents: false,
        fetchingMembers: false,
        fetchingFilterGroups: false,
        globalRerenderCount: 0,
        allLocations: [],
        filterGroups: [],
    };
}

export const useBrandingStore = defineStore('branding', {
    // IMPORTANT: state must be a function so the module can be
    // instantiated multiple times
    // https://ssr.vuejs.org/guide/structure.html#avoid-stateful-singletons
    state: () => ({
        ...initialState(),
        commonStore: useCommonStore()
    }),
    getters: {
        company() {
            return this.commonStore.currentCompany;
        },
        'brandHSLA': (state) => {
            return 'hsla(' +
                state.settings.primary_color.h + ', ' +
                state.settings.primary_color.s * 100 + '%, ' +
                state.settings.primary_color.l * 100 + '%, ' +
                state.settings.primary_color.a + ')';
        },
        'brandHex': (state) => {
            return tinycolor(state.settings.primary_color).toHexString();
        },
        'complementBrandHex': (state, getters) => {
            return tinycolor(state.settings.primary_color).spin(180).toHexString();
        },
        'brand10HSLA': (state) => {
            return 'hsla(' +
                state.settings.primary_color.h + ', ' +
                state.settings.primary_color.s * 100 + '%, ' +
                state.settings.primary_color.l * 100 + '%, 0.1)';
        },
        'brand30HSLA': (state) => {
            return 'hsla(' +
                state.settings.primary_color.h + ', ' +
                state.settings.primary_color.s * 100 + '%, ' +
                state.settings.primary_color.l * 100 + '%, 0.3)';
        },
        'brandContrastHEX': (state) => {
            const brandColor = tinycolor(state.settings.primary_color);
            const contrastColor = brandColor.getLuminance() > 0.40
                ? brandColor.darken(80)
                : brandColor.lighten(95);

            return contrastColor.desaturate(20).toHexString();
        },
        'textContrastHEX': (state) => {
            const textColor = tinycolor(state.settings.secondary_color);
            const contrastColor = textColor.getLuminance() > 0.40
                ? textColor.darken(80)
                : textColor.lighten(95);

            return contrastColor.desaturate(20).toHexString();
        },
        'textHSLA': (state) => {
            return 'hsla(' +
                state.settings.secondary_color.h + ', ' +
                state.settings.secondary_color.s * 100 + '%, ' +
                state.settings.secondary_color.l * 100 + '%, ' +
                state.settings.secondary_color.a + ')';
        },
        'textLightHex': (state) => {
            let color = tinycolor(state.settings.secondary_color);
            color = color.desaturate(color.toHsl().s * 75);

            const brightnessAdjustment = 85 - (color.getBrightness() / 2.55);

            if (brightnessAdjustment >= 0) {
                color = color.brighten(brightnessAdjustment);
            } else {
                color = color.darken(Math.abs(brightnessAdjustment));
            }

            return color.toHexString();
        },
        'text50HSLA': (state) => {
            return 'hsla(' +
                state.settings.secondary_color.h + ', ' +
                state.settings.secondary_color.s * 100 + '%, ' +
                state.settings.secondary_color.l * 100 + '%, 0.5)';
        },
        'text70HSLA': (state) => {
            return 'hsla(' +
                state.settings.secondary_color.h + ', ' +
                state.settings.secondary_color.s * 100 + '%, ' +
                state.settings.secondary_color.l * 100 + '%, 0.7)';
        },
        'text80HSLA': (state) => {
            return 'hsla(' +
                state.settings.secondary_color.h + ', ' +
                state.settings.secondary_color.s * 100 + '%, ' +
                state.settings.secondary_color.l * 100 + '%, 0.8)';
        },
        components() {
            const components = useRepo(Component)
                .withAllRecursive(1)
                .get()
                .sort((a, b) => a.order - b.order);
            return Component.withSortedItems(components);
        },
        'displayFont': state => state.settings.fonts.display,
        'textFont': state => state.settings.fonts.text,
    },
    actions: {
        forceRerender() {
            this.incrementRenders();

            return useRepo(Component).update({
                where: () => true,
                rerenderCount: (x) => x + 1
            });
        },
        insertComponents(components) {
            return useRepo(Component).save(normalizeJson(components));
        },
        fetchComponents() {
            this.startFetchingComponents();

            return components.index()
                .then(result => {
                    return this.insertComponents(result.data);
                })
                .then(() => {
                    this.stopFetchingComponents();
                });
        },
        fetchFilterGroups() {
            this.toggleFetchingFilterGroups(true);

            return jobs.filterGroups()
                .then(result => {
                    return this.insertFilterGroups(result.data);
                })
                .then(() => {
                    this.toggleFetchingFilterGroups(false);
                });
        },
        insertFilterGroups(filterGroups) {
            return useRepo(FilterGroup).save(normalizeJson(filterGroups));
        },
        resetState() {
            Object.assign(this, initialState());
        },
        startAddingComponent(position) {
            this.addingComponent = position;
        },
        cancelAddingComponent() {
            this.addingComponent = false;
        },
        startConfiguringComponent(component) {
            this.addingComponent = false;
            this.configuringComponent = component;
        },
        cancelConfiguringComponent() {
            this.configuringComponent = false;
        },
        stopConfiguringComponent() {
            this.configuringComponent = false;
        },
        startConfiguringHeader() {
            this.configuringHeader = true;
        },
        cancelConfiguringHeader() {
            this.configuringHeader = false;
        },
        stopConfiguringHeader() {
            this.configuringHeader = false;
        },
        startFetchingComponents() {
            this.fetchingComponents = true;
        },
        stopFetchingComponents() {
            this.fetchingComponents = false;
        },
        startFetchingMembers() {
            this.fetchingMembers = true;
        },
        stopFetchingMembers() {
            this.fetchingMembers = false;
        },
        toggleFetchingFilterGroups(fetching = true) {
            this.fetchingFilterGroups = fetching;
        },
        setSettings({ rounding, padding, display_font, text_font, primary_color, secondary_color }) {
            this.settings.rounding = rounding;
            this.settings.padding = padding;
            this.settings.display_font = display_font;
            this.settings.text_font = text_font;
            this.settings.primary_color = primary_color;
            this.settings.secondary_color = secondary_color;
        },
        setPrimaryColor(primary_color) {
            this.settings.primary_color = primary_color;
        },
        setSecondaryColor(secondary_color) {
            this.settings.secondary_color = secondary_color;
        },
        incrementRenders() {
            this.globalRerenderCount++;
        },
        setAllLocations(locations) {
            this.allLocations = locations;
        }
    }
});
